import React, { useEffect, useMemo, useState } from "react";
import { useIdleExempt } from "../hooks/useIdleExempt";
import axios from "axios";
import {
  Users as UsersIcon, Plus, Pencil, Power, PowerOff, KeyRound, Search,
  ShieldCheck, X, Loader2, Trash2, AlertTriangle, Send, Check, Mail,
} from "lucide-react";
import { useAuth, isPlatformAdmin, isPlatformOrFirmAdmin, assocAdminAids } from "../contexts/AuthContext";
import ConfirmDialog from "../components/ConfirmDialog";
import { retryOnReloadHiccup } from "../utils/retryOnReloadHiccup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step 8 (2026-08-03) — Users & Memberships management UI. Consumes the
// nine Platform-Admin-only endpoints shipped in Step 7. Layout mirrors
// FirmsScreen for visual consistency.

// 2026-03-09 (Dave) — Central extractor for axios error responses.
// FastAPI usually returns `{detail: "..."}` on HTTPException, but
// Pydantic validation errors return `{detail: [{loc, msg, type}, ...]}`,
// and true network failures produce no `response` at all. This helper
// gives us a single, human-readable string for the error banner no
// matter which shape came back — so add/patch/revoke all surface the
// real cause instead of a generic "Add failed."
const apiDetail = (e, fallback = "Request failed.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) {
    // Pydantic v2 shape: [{msg: "...", loc: [...]}, ...]
    return d
      .map((it) => (typeof it === "string" ? it : (it?.msg || JSON.stringify(it))))
      .join("; ");
  }
  if (d && typeof d === "object") {
    return d.msg || d.message || JSON.stringify(d);
  }
  if (e?.message && !/^Request failed with status/i.test(e.message)) {
    return e.message;                       // e.g. "Network Error"
  }
  return fallback;
};

const ROLE_LABEL = {
  administrator: "Administrator",
  analyst: "Analyst",
  view_only: "View-Only",
};

const ROLE_STYLE = {
  administrator: { bg: "#E9F1F9", fg: "#1C4E80", border: "#1C4E80" },
  analyst:       { bg: "#EAF6EF", fg: "#2E7D5B", border: "#2E7D5B" },
  view_only:     { bg: "#F1F3F5", fg: "#5A6B75", border: "#B8C1CB" },
};

function RoleBadge({ role }) {
  const s = ROLE_STYLE[role] || ROLE_STYLE.view_only;
  return (
    <span
      className="inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] tracking-[0.1em] uppercase font-bold rounded-[3px]"
      style={{
        background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
        // 2026-02-19 (Dave) — uniform width so pills align vertically down
        // the Role column. Widest label is "Administrator"; the shorter
        // ones (Analyst, View-Only) center within the same footprint.
        minWidth: 100,
      }}
    >
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function IconBtn({ children, onClick, disabled, title, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1.5 border border-rule rounded-[3px] ${danger ? "text-coral hover:border-coral" : "text-slate hover:border-navy hover:text-navy"} disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
      style={{ minHeight: 32, minWidth: 32 }}
    >
      {children}
    </button>
  );
}

// ---------- New / Edit user modal -----------------------------------------
function UserEditorModal({ initial, allFirms, allAssocs, actorIsPlatform, assocOnlyAids, onClose, onSaved, onOpenMemberships }) {
  const isEdit = !!initial;
  useIdleExempt(!isEdit);
  const [email, setEmail] = useState(initial?.email || "");
  const [firstName, setFirstName] = useState(initial?.first_name ?? splitName(initial?.full_name)[0]);
  const [lastName, setLastName] = useState(initial?.last_name ?? splitName(initial?.full_name)[1]);
  const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
  const [role, setRole] = useState(initial ? effectiveRole(initial) : "view_only");
  // 2026-02-25 (Dave) — every new user must land with at least ONE
  // scoped membership. The picker has three flavors:
  //   • tenant     → Firm Admin scope (a specific firm)
  //   • client     → Association Admin scope (a specific association)
  //   • platform   → Platform Admin (visible only to Platform Admins)
  // Historically the modal only offered tenant/platform which meant
  // choosing Administrator + a firm accidentally made the user a
  // FULL Firm Admin. Adding the client scope lets an admin scope the
  // Administrator role to a single association without granting firm-
  // wide power. See conversation with Dave on 2026-02-25.
  const [scopeType, setScopeType] = useState(assocOnlyAids ? "client" : "tenant");
  const [scopeSel, setScopeSel] = useState("");            // tenant_id (when scopeType==="tenant")
  const [associationSel, setAssociationSel] = useState(""); // aid (when scopeType==="client")
  const [associations, setAssociations] = useState([]);     // fetched on mount for the picker
  const [assocLoading, setAssocLoading] = useState(false);
  const [membershipRole, setMembershipRole] = useState("view_only");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(false);
  // 2026-02-23 (Dave) — When the entered email already belongs to a
  // user in the system, the backend returns a structured 409 with the
  // existing user's id + name. Instead of a dead-end error, offer a
  // one-click follow-up that adds a membership to the existing user
  // (same firm + role the admin already selected).
  const [existingUser, setExistingUser] = useState(null);

  // Build the membership object based on the current scope selection.
  // Encapsulated here so both `submit` and `attachToExisting` produce
  // identical payloads (bug shield against drift).
  const buildMembership = () => {
    if (scopeType === "platform") {
      return { scope_type: "platform", scope_id: "00000000-0000-0000-0000-000000000000", role: membershipRole };
    }
    if (scopeType === "client") {
      return { scope_type: "client", scope_id: associationSel, role: membershipRole };
    }
    return { scope_type: "tenant", scope_id: scopeSel, role: membershipRole };
  };

  // Fetch associations once when the modal opens. Needed by both:
  //   • create mode → populates the "Association" scope picker
  //   • edit mode → so we can render each membership's scope name
  useEffect(() => {
    let cancelled = false;
    setAssocLoading(true);
    axios.get(`${API}/associations`)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        // Association Admins can only add users to associations they administer.
        setAssociations(assocOnlyAids ? list.filter((a) => assocOnlyAids.has(String(a.aid))) : list);
      })
      .catch(() => { if (!cancelled) setAssociations([]); })
      .finally(() => { if (!cancelled) setAssocLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const attachToExisting = async () => {
    if (!existingUser) return;
    setSaving(true);
    setErr("");
    try {
      await axios.post(
        `${API}/admin/users/${existingUser.existing_user_id}/memberships`,
        buildMembership(),
      );
      setExistingUser(null);
      setCreated(true);
      onSaved();
    } catch (e) {
      setErr(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : e?.message || "Add-to-firm failed.",
      );
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setErr("");
    if (!firstName.trim() || !lastName.trim()) { setErr("First and last name are required."); return; }
    // 2026-02-25 (Dave) — email is now OPTIONAL on create. Admins can
    // pre-provision users (name, role, memberships) and add the email
    // later. Send Invite is a separate action on the edit modal.
    if (email.trim() && !email.includes("@")) {
      setErr("Email must be a valid address or left blank.");
      return;
    }
    if (!isEdit) {
      if (scopeType === "tenant" && !scopeSel) {
        setErr("Pick a firm before creating."); return;
      }
      if (scopeType === "client" && !associationSel) {
        setErr("Pick an association before creating."); return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        // On edit, send explicit null when email is cleared so the
        // backend removes it (belt-and-suspenders with the Pydantic
        // model_fields_set logic on the server).
        const trimmedEmail = email.trim().toLowerCase();
        const currentEmail = (initial?.email || "").toLowerCase();
        const emailChanged = trimmedEmail !== currentEmail;
        const patch = { first_name: firstName.trim(), last_name: lastName.trim(), role };
        if (emailChanged) patch.email = trimmedEmail || null;
        await axios.patch(`${API}/admin/users/${initial.user_id}`, patch);
        // Role dropdown governs real permissions — sync firm/association memberships.
        if (role !== effectiveRole(initial)) {
          const toSync = (initial.memberships || []).filter(
            (m) => m.scope_type !== "platform" && m.role !== role,
          );
          for (const m of toSync) {
            await axios.patch(`${API}/admin/memberships/${m.membership_id}`, { role });
          }
        }
        onSaved();
        onClose();
      } else {
        const membership = buildMembership();
        await axios.post(`${API}/admin/users`, {
          email: email.trim() ? email.trim().toLowerCase() : null,
          full_name: fullName, first_name: firstName.trim(), last_name: lastName.trim(), role: membershipRole,
          must_change_password: true,
          initial_memberships: [membership],
        });
        setCreated(true);
        onSaved();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_in_use") {
        setExistingUser(detail);
        setErr("");
      } else {
        setErr(
          (typeof detail === "string" && detail)
          || e.message
          || "Save failed.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // 2026-02-25 (Dave) — Manual invite trigger on the edit modal. Only
  // enabled when the user has an email and is active. Regenerates temp
  // password and dispatches the Postmark invite email.
  const [inviting, setInviting] = useState(false);
  const [inviteSentAt, setInviteSentAt] = useState(null);
  const sendInvite = async () => {
    if (!isEdit || !initial?.user_id) return;
    setInviting(true); setErr("");
    try {
      await axios.post(`${API}/admin/users/${initial.user_id}/send-invite`);
      setInviteSentAt(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr((typeof detail === "string" && detail) || e.message || "Send invite failed.");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(15,42,68,0.55)" }} onClick={onClose}>
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[520px] max-w-[92vw] rounded-[8px]"
        style={{ borderRadius: 8, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="user-editor-modal"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash/40">
          <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">
            {isEdit ? "Edit User" : (created ? "Invite Sent" : "New User")}
          </div>
          <button onClick={onClose} className="text-slate hover:text-navy" data-testid="user-editor-close">
            <X size={16} />
          </button>
        </div>

        {created ? (
          <div className="p-5">
            <div className="text-[12.5px] text-ink leading-relaxed mb-3">
              <span className="font-semibold">{fullName}</span>{email ? ` (${email})` : ""} has been created.
              {email ? " Press the Send Invite button on their profile when you're ready to email their temporary password." : " Add an email address on their profile when you have it, then press Send Invite."}
            </div>
            <p className="text-[11px] text-slate leading-relaxed" data-testid="user-editor-postmark-note">
              An invitation is <span className="font-semibold">NOT auto-sent</span> — the admin controls the moment.
            </p>
            <div className="mt-4 flex justify-end">
              <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B]" data-testid="user-editor-done">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["First name", firstName, setFirstName, "user-editor-first-name"],
                  ["Last name", lastName, setLastName, "user-editor-last-name"],
                ].map(([label, val, setVal, tid]) => (
                  <label key={tid} className="flex flex-col gap-1">
                    <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                      {label} <span className="text-coral">*</span>
                    </span>
                    <input
                      type="text"
                      disabled={saving}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      data-testid={tid}
                      className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
                    />
                  </label>
                ))}
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                  Email <span className="text-slate/70 font-normal normal-case tracking-normal">(optional — add later if unknown)</span>
                </span>
                <input
                  type="email"
                  disabled={saving}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  data-testid="user-editor-email"
                  className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy disabled:bg-navy-wash/40 disabled:cursor-not-allowed"
                />
              </label>
              {/* 2026-02-19 (Dave) — top-level Role dropdown is EDIT-ONLY.
                  For a new user, the membership role below is the single
                  source of truth (and is mirrored onto the top-level
                  user.role in the submit handler). Two identical Role
                  fields on the same form was confusing. */}
              {isEdit && (
                <label className="flex flex-col gap-1">
                  <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">Role</span>
                  <select
                    disabled={saving}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid="user-editor-role"
                    className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
                  >
                    <option value="administrator">Administrator</option>
                    <option value="analyst">Analyst</option>
                    <option value="view_only">View-Only</option>
                  </select>
                </label>
              )}
              {isEdit && initial?.memberships && (
                <div className="flex flex-col gap-1.5" data-testid="user-editor-memberships-block">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                      Assignments
                    </span>
                    <button
                      type="button"
                      onClick={() => onOpenMemberships?.(initial)}
                      className="text-[10.5px] text-navy underline underline-offset-2 hover:text-coral"
                      data-testid="user-editor-open-memberships"
                    >
                      Edit accounts →
                    </button>
                  </div>
                  {initial.memberships.length === 0 ? (
                    <div className="text-[11.5px] text-slate italic">
                      No assignments yet. Click <span className="font-semibold">Edit accounts</span> to add one.
                    </div>
                  ) : (
                    <div
                      className="flex flex-col gap-1 border border-rule rounded-[3px] bg-navy-wash/40 p-2 max-h-[160px] overflow-y-auto"
                      data-testid="user-editor-memberships-list"
                    >
                      {initial.memberships.map((m) => {
                        let scopeName;
                        let scopeKind;
                        if (m.scope_type === "platform") {
                          scopeName = "Platform (uReserve staff)";
                          scopeKind = "Platform";
                        } else if (m.scope_type === "tenant") {
                          const f = (allFirms || []).find((x) => x.tenant_id === m.scope_id);
                          scopeName = f?.name || `Firm ${String(m.scope_id).slice(0, 8)}…`;
                          scopeKind = "Firm";
                        } else if (m.scope_type === "client") {
                          // 2026-02-25 (Dave) — prefer the screen-level
                          // `allAssocs` list which is not tenant-filtered
                          // so association-only users still resolve to
                          // a real name here.
                          const a = (allAssocs || []).find((x) => x.aid === m.scope_id)
                                 || associations.find((x) => x.aid === m.scope_id);
                          scopeName = a?.name || `Association ${String(m.scope_id).slice(0, 8)}…`;
                          scopeKind = "Association";
                        } else {
                          scopeName = String(m.scope_id).slice(0, 8) + "…";
                          scopeKind = m.scope_type;
                        }
                        const roleLabel = ({
                          administrator: "Administrator",
                          analyst: "Analyst",
                          view_only: "View-Only",
                        })[m.role] || m.role;
                        return (
                          <div
                            key={m.membership_id || `${m.scope_type}-${m.scope_id}`}
                            className="flex items-center gap-2 text-[11.5px]"
                            data-testid={`user-editor-membership-${m.scope_type}-${m.scope_id}`}
                          >
                            <span
                              className="inline-block px-1.5 py-0.5 text-[9.5px] tracking-[0.06em] uppercase font-bold text-navy bg-white border border-navy rounded-[3px] shrink-0"
                              style={{ minWidth: 78, textAlign: "center" }}
                            >
                              {scopeKind}
                            </span>
                            <span className="text-ink truncate flex-1" title={scopeName}>
                              {scopeName}
                            </span>
                            <span className="text-slate text-[10.5px] shrink-0">
                              {roleLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {!isEdit && (
                <>
                  {/* 2026-02-25 (Dave) — scope-TYPE selector first, then
                      the scope-value picker relevant to that type. This
                      solves the earlier logic flaw where "Administrator
                      + Firm X" accidentally made the user a full Firm
                      Admin instead of an Association Admin. */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                      Assign to <span className="text-coral">*</span>
                    </span>
                    <div className="flex items-center gap-3 text-[12px] text-ink" data-testid="user-editor-scope-type">
                      {!assocOnlyAids && <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="scope-type"
                          value="tenant"
                          checked={scopeType === "tenant"}
                          onChange={() => { setScopeType("tenant"); setAssociationSel(""); }}
                          disabled={saving}
                          data-testid="user-editor-scope-type-firm"
                        />
                        Firm
                      </label>}
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="scope-type"
                          value="client"
                          checked={scopeType === "client"}
                          onChange={() => { setScopeType("client"); setScopeSel(""); }}
                          disabled={saving}
                          data-testid="user-editor-scope-type-association"
                        />
                        Association
                      </label>
                      {actorIsPlatform && (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="scope-type"
                            value="platform"
                            checked={scopeType === "platform"}
                            onChange={() => { setScopeType("platform"); setScopeSel(""); setAssociationSel(""); }}
                            disabled={saving}
                            data-testid="user-editor-scope-type-platform"
                          />
                          Platform (uReserve staff)
                        </label>
                      )}
                    </div>
                  </div>

                  {scopeType === "tenant" && (
                    <label className="flex flex-col gap-1">
                      <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                        Firm <span className="text-coral">*</span>
                      </span>
                      <select
                        disabled={saving}
                        value={scopeSel}
                        onChange={(e) => setScopeSel(e.target.value)}
                        data-testid="user-editor-scope"
                        className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
                      >
                        <option value="">— Select firm —</option>
                        {(allFirms || []).filter((f) => f.is_active !== false).map((f) => (
                          <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
                        ))}
                      </select>
                      <span className="text-[10.5px] text-slate italic">
                        Administrator on a firm = FULL Firm Admin over every association under it. Pick &quot;Association&quot; instead to scope the admin role to a single association.
                      </span>
                    </label>
                  )}

                  {scopeType === "client" && (
                    <label className="flex flex-col gap-1">
                      <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
                        Association <span className="text-coral">*</span>
                      </span>
                      <select
                        disabled={saving || assocLoading}
                        value={associationSel}
                        onChange={(e) => setAssociationSel(e.target.value)}
                        data-testid="user-editor-scope-association"
                        className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
                      >
                        <option value="">{assocLoading ? "Loading associations…" : "— Select association —"}</option>
                        {associations.map((a) => {
                          const firm = (allFirms || []).find((f) => f.tenant_id === a.tenant_id);
                          const suffix = firm ? ` — ${firm.name}` : "";
                          return (
                            <option key={a.aid} value={a.aid}>
                              {a.name}{suffix}
                            </option>
                          );
                        })}
                      </select>
                      <span className="text-[10.5px] text-slate italic">
                        Administrator here = admin of THIS association only, not the whole firm.
                      </span>
                    </label>
                  )}

                  <label className="flex flex-col gap-1">
                    <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">Role</span>
                    <select
                      disabled={saving}
                      value={membershipRole}
                      onChange={(e) => setMembershipRole(e.target.value)}
                      data-testid="user-editor-membership-role"
                      className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
                    >
                      <option value="administrator">Administrator</option>
                      <option value="analyst">Analyst</option>
                      <option value="view_only">View-Only</option>
                    </select>
                  </label>
                </>
              )}
              {err && (
                <div className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2]" data-testid="user-editor-error">
                  {err}
                </div>
              )}
              {/* 2026-02-23 (Dave) — email_in_use follow-up prompt. */}
              {existingUser && (
                <div
                  className="text-[11.5px] text-navy border border-navy px-3 py-2 rounded-[3px] bg-navy-wash/60"
                  data-testid="user-editor-existing-prompt"
                >
                  <div className="font-semibold mb-1">Email already in use</div>
                  <div className="mb-2 leading-snug">
                    <b>{existingUser.existing_full_name}</b> ({existingUser.existing_email})
                    already has an account.
                    {!existingUser.existing_is_active && (
                      <span className="text-coral"> This account is currently inactive.</span>
                    )}
                    {" "}Add them to this firm?
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setExistingUser(null)}
                      disabled={saving}
                      data-testid="user-editor-existing-cancel"
                      className="px-2 py-1 text-[11px] text-slate hover:text-navy"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={attachToExisting}
                      disabled={saving || !existingUser.existing_is_active}
                      data-testid="user-editor-existing-attach"
                      className="px-2.5 py-1 text-[11px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 size={11} className="animate-spin" /> : null}
                      Add to this firm
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash/40">
              {/* Left side of footer — Send Invite (edit-mode only). */}
              <div className="flex items-center gap-2">
                {isEdit && (
                  <>
                    <button
                      type="button"
                      onClick={sendInvite}
                      disabled={inviting || saving || !email.trim() || (initial && !initial.is_active)}
                      data-testid="user-editor-send-invite"
                      data-loading={inviting ? "true" : undefined}
                      title={
                        !email.trim() ? "Add an email address first."
                          : (initial && !initial.is_active) ? "Reactivate the user first."
                          : "Email the user their temporary password and sign-in link."
                      }
                      // 2026-02-26 (Dave) — flicker fix. Previously the
                      // button used one class list with `hover:bg-coral
                      // hover:text-white` + `disabled:hover:bg-transparent
                      // disabled:hover:text-coral`. On click, `inviting`
                      // flips the disabled bit while the cursor is still
                      // over the button, so the styles cross-fade coral→
                      // transparent (bg) and white→coral (text)
                      // simultaneously — for a frame or two the bg is
                      // already ~white while the text is still ~white
                      // (the "white on white" pop). Now the loading
                      // state is opt-out: while `inviting` we lock the
                      // button to a solid coral fill + white text + wait
                      // cursor — no transitions, no hover overrides.
                      // The transparent/coral resting look and the
                      // opacity-40 disabled look only apply when NOT
                      // loading.
                      className={
                        "inline-flex items-center gap-1.5 px-2.5 py-1 text-[11.5px] font-semibold border border-coral rounded-[3px] " +
                        (inviting
                          ? "bg-coral text-[#1A1A1A] cursor-wait"
                          : "text-[#1A1A1A] hover:bg-coral transition-[background-color] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent")
                      }
                    >
                      {inviting ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                      {inviteSentAt ? "Resend Invite" : "Send Invite"}
                    </button>
                    {inviteSentAt && (
                      <span
                        className="inline-flex items-center gap-1 text-[10.5px] text-[#1F7A3A]"
                        data-testid="user-editor-invite-sent"
                      >
                        <Check size={10} /> Sent
                      </span>
                    )}
                  </>
                )}
              </div>
              {/* Right side of footer — Cancel + primary save. */}
              <div className="flex items-center gap-2">
                <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-[12px] text-slate hover:text-navy" data-testid="user-editor-cancel">Cancel</button>
                <button
                  onClick={submit}
                  disabled={saving}
                  data-testid="user-editor-save"
                  className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 flex items-center gap-1.5"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                  {isEdit ? "Save" : "Create"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Reset password modal ------------------------------------------
function ResetPasswordModal({ user, onClose }) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const doReset = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/admin/users/${user.user_id}/reset-password`);
      setDone(true);
    } catch (e) {
      setErr(apiDetail(e, "Reset failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(15,42,68,0.55)" }} onClick={onClose}>
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[500px] max-w-[92vw] rounded-[8px]"
        style={{ borderRadius: 8, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reset-password-modal"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash/40">
          <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">Reset Password</div>
          <button onClick={onClose} className="text-slate hover:text-navy" data-testid="reset-password-close"><X size={16} /></button>
        </div>
        <div className="p-5">
          <div className="text-[12.5px] text-ink leading-relaxed mb-4">
            {done ? (
              <>A new temporary password has been emailed to <span className="font-semibold">{user.full_name}</span> ({user.email}). Their previous password no longer works and they&apos;ll be prompted to set a new one on next login.</>
            ) : (
              <>Generate a new temporary password for <span className="font-semibold">{user.full_name}</span> ({user.email}). It will be emailed to them immediately; their current password will stop working, and they&apos;ll be forced to change the new one on next login.</>
            )}
          </div>
          {!done && (
            <>
              {err && <div className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2] mb-3" data-testid="reset-password-error">{err}</div>}
              <div className="text-[11.5px] text-coral font-semibold flex items-center gap-1.5">
                <ShieldCheck size={13} /> This action is logged to the audit trail.
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash/40">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-[12px] text-slate hover:text-navy" data-testid="reset-password-cancel">
            {done ? "Done" : "Cancel"}
          </button>
          {!done && (
            <button
              type="button"
              onClick={doReset}
              disabled={busy}
              data-testid="reset-password-confirm"
              className="px-3 py-1.5 text-[12px] font-semibold text-white bg-coral rounded-[3px] ring-2 ring-[#EA6A47]/50 ring-offset-2 shadow-md hover:bg-[#E85A42] disabled:opacity-40 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
              Reset & Email Password
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Memberships drawer -------------------------------------------
function MembershipsDrawer({ user, allFirms, assocsAll, assocOnlyAids, onClose, onChanged }) {
  const { user: me } = useAuth();
  const actorIsPlatform = isPlatformAdmin(me);
  const PLATFORM_SCOPE_ID = "00000000-0000-4000-8000-000000000001";

  const [rows, setRows] = useState(user.memberships || []);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // 2026-02-19 (Dave) — scope-type toggle. Platform Admins can grant
  // platform-scope memberships from the same form; everyone else only
  // sees the firm flow (unchanged from before).
  const [addScopeType, setAddScopeType] = useState(assocOnlyAids ? "client" : "tenant");
  const [addScopeId, setAddScopeId] = useState("");
  const [addRole, setAddRole] = useState("view_only");
  const [err, setErr] = useState("");
  // 2026-02-16 (Dave) — branded confirm replaces window.confirm() for revoke.
  const [removing, setRemoving] = useState(null); // membership row or null
  // 2026-02-19 (Dave) — confirmation step for granting Platform-scope
  // memberships. High-power role → require an explicit second click.
  const [confirmingPlatformGrant, setConfirmingPlatformGrant] = useState(false);

  const refresh = async () => {
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}`);
      setRows(res.data.memberships || []);
      onChanged();
    } catch (e) {
      setErr(apiDetail(e, "Refresh failed."));
    }
  };

  const removeRow = (m) => {
    setRemoving(m);
  };

  const confirmRemoveMembership = async () => {
    const m = removing;
    if (!m) return;
    setBusy(true); setErr("");
    try {
      await axios.delete(`${API}/admin/memberships/${m.membership_id}`);
      setRemoving(null);
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Remove failed.";
      setErr(msg);
      throw new Error(msg);
    } finally { setBusy(false); }
  };

  const changeRole = async (mid, role) => {
    setBusy(true); setErr("");
    try {
      await axios.patch(`${API}/admin/memberships/${mid}`, { role });
      await refresh();
    } catch (e) {
      setErr(apiDetail(e, "Update failed."));
    } finally { setBusy(false); }
  };

  const add = async () => {
    // Route by scope type. Platform grants use a fixed scope_id; firm
    // grants require the user to have picked one from the dropdown.
    if (addScopeType === "tenant" && !addScopeId) {
      setErr("Pick a firm to grant access to.");
      return;
    }
    if (addScopeType === "client" && !addScopeId) {
      setErr("Pick an association to grant access to.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const payload = addScopeType === "platform"
        ? { scope_type: "platform", scope_id: PLATFORM_SCOPE_ID, role: addRole }
        : { scope_type: addScopeType, scope_id: addScopeId, role: addRole };
      await axios.post(`${API}/admin/users/${user.user_id}/memberships`, payload);
      setAddOpen(false);
      setAddScopeType("tenant");
      setAddScopeId("");
      setAddRole("view_only");
      setConfirmingPlatformGrant(false);
      await refresh();
    } catch (e) {
      setErr(apiDetail(e, "Add failed."));
    } finally { setBusy(false); }
  };

  // 2026-02-19 (Dave) — one place that decides whether the Save click
  // should route directly to `add()` or first open the "you're granting
  // Platform Admin" speed bump. Called from the button click.
  const handleSaveClick = () => {
    if (addScopeType === "platform" && addRole === "administrator") {
      setConfirmingPlatformGrant(true);
      return;
    }
    add();
  };

  const firmName = (sid) => {
    const f = allFirms.find((x) => x.tenant_id === sid);
    return f ? f.name : sid.slice(0, 8) + "…";
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(15,42,68,0.35)" }} onClick={onClose}>
      <div
        className="bg-white h-full w-[560px] max-w-[92vw] border-l-2 border-navy shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="memberships-drawer"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-rule bg-navy-wash/40">
          <div>
            <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">Accounts</div>
            <div className="text-[11.5px] text-slate mt-0.5">{user.full_name} · {user.email}</div>
          </div>
          <button onClick={onClose} className="text-slate hover:text-navy" data-testid="memberships-close"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {err && <div className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2] mb-3" data-testid="memberships-error">{err}</div>}
          {rows.length === 0 && !addOpen && (
            <div className="text-[12px] text-slate italic mb-4">
              No accounts yet. This user cannot see any firm data.
            </div>
          )}
          <ul className="flex flex-col gap-2">
            {rows.map((m) => {
              // 2026-02-25 (Dave) — resolve association names for
              // client-scope memberships (previously showed a truncated
              // aid). Firm memberships resolve to the firm name; the
              // platform row keeps its special label.
              let title;
              let subtitle;
              if (m.scope_type === "platform") {
                title = "uReserve Platform";
                subtitle = "platform-wide";
              } else if (m.scope_type === "tenant") {
                title = firmName(m.scope_id) || `Firm ${m.scope_id.slice(0, 8)}…`;
                subtitle = "firm-wide";
              } else if (m.scope_type === "client") {
                const a = (assocsAll || []).find((x) => x.aid === m.scope_id);
                if (a) {
                  const firmLbl = firmName(a.tenant_id);
                  title = a.name;
                  subtitle = firmLbl ? `Association · ${firmLbl}` : "Association";
                } else {
                  title = `Association ${m.scope_id.slice(0, 8)}…`;
                  subtitle = "Association";
                }
              } else {
                title = m.scope_id.slice(0, 8) + "…";
                subtitle = m.scope_type;
              }
              return (
              <li key={m.membership_id} className="flex items-center gap-3 border border-rule rounded-[3px] px-3 py-1" data-testid={`membership-row-${m.membership_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-ink font-semibold truncate" title={title}>
                    {title}
                  </div>
                  <div className="text-[10.5px] text-slate truncate">{subtitle}</div>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => changeRole(m.membership_id, e.target.value)}
                  disabled={busy}
                  className="border border-rule rounded-[3px] px-2 py-1 text-[12px]"
                  data-testid={`membership-role-${m.membership_id}`}
                >
                  <option value="administrator">Administrator</option>
                  <option value="analyst">Analyst</option>
                  <option value="view_only">View-Only</option>
                </select>
                <IconBtn onClick={() => removeRow(m)} disabled={busy} title="Revoke" danger testid={`membership-remove-${m.membership_id}`}>
                  <Trash2 size={12} />
                </IconBtn>
              </li>
              );
            })}
          </ul>

          {addOpen ? (
            <div className="mt-4 border border-navy rounded-[3px] p-3 bg-navy-wash/30" data-testid="membership-add-form">
              <div className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate mb-2">
                {addScopeType === "platform" ? "Grant Platform Access" : addScopeType === "client" ? "Grant Access to One Association" : "Grant Access to a Firm"}
              </div>

              {/* Scope-type toggle — Platform option only shown to Platform Admins. */}
              {(
                <div className="flex gap-2 mb-2" data-testid="membership-add-scope-type">
                  {!assocOnlyAids && <button
                    type="button"
                    onClick={() => { setAddScopeType("tenant"); setAddScopeId(""); }}
                    disabled={busy}
                    data-testid="scope-type-tenant"
                    className={`flex-1 px-2 py-1 text-[11.5px] font-semibold rounded-[3px] border ${addScopeType === "tenant" ? "bg-navy text-white border-navy" : "text-slate border-rule hover:border-navy"}`}
                  >Firm</button>}
                  <button
                    type="button"
                    onClick={() => { setAddScopeType("client"); setAddScopeId(""); }}
                    disabled={busy}
                    data-testid="scope-type-association"
                    className={`flex-1 px-2 py-1 text-[11.5px] font-semibold rounded-[3px] border ${addScopeType === "client" ? "bg-navy text-white border-navy" : "text-slate border-rule hover:border-navy"}`}
                  >Association</button>
                  {actorIsPlatform && <button
                    type="button"
                    onClick={() => { setAddScopeType("platform"); setAddScopeId(""); }}
                    disabled={busy}
                    data-testid="scope-type-platform"
                    className={`flex-1 px-2 py-1 text-[11.5px] font-semibold rounded-[3px] border ${addScopeType === "platform" ? "bg-navy text-white border-navy" : "text-slate border-rule hover:border-navy"}`}
                  >Platform</button>}
                </div>
              )}

              <div className="flex flex-col gap-2">
                {addScopeType === "tenant" ? (
                  <select
                    value={addScopeId}
                    onChange={(e) => setAddScopeId(e.target.value)}
                    className="border border-rule rounded-[3px] px-2 py-1.5 text-[12.5px]"
                    data-testid="membership-add-scope"
                  >
                    <option value="">— Select firm —</option>
                    {allFirms.map((f) => (
                      <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
                    ))}
                  </select>
                ) : addScopeType === "client" ? (
                  <>
                    <select
                      value={addScopeId}
                      onChange={(e) => setAddScopeId(e.target.value)}
                      className="border border-rule rounded-[3px] px-2 py-1.5 text-[12.5px]"
                      data-testid="membership-add-association"
                    >
                      <option value="">— Select association —</option>
                      {(assocsAll || []).filter((a) => !assocOnlyAids || assocOnlyAids.has(String(a.aid))).map((a) => {
                        const firm = allFirms.find((f) => f.tenant_id === a.tenant_id);
                        return (
                          <option key={a.aid} value={a.aid}>{a.name}{firm ? ` — ${firm.name}` : ""}</option>
                        );
                      })}
                    </select>
                    <span className="text-[10.5px] text-slate italic">
                      Access to THIS association only, not the whole firm.
                    </span>
                  </>
                ) : (
                  /* Platform caution banner — plain-English warning that
                     platform access is the highest-power role in the system. */
                  <div
                    className="border border-coral bg-[#FEF5F2] rounded-[3px] px-2 py-1.5 text-[11.5px] text-ink"
                    data-testid="platform-caution-banner"
                  >
                    <div className="font-semibold text-coral mb-0.5">
                      Platform Admins have full access to every firm, association, and setting.
                    </div>
                    <div className="text-slate">
                      Assign sparingly. This gives them everything you have.
                    </div>
                  </div>
                )}
                <select
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value)}
                  className="border border-rule rounded-[3px] px-2 py-1.5 text-[12.5px]"
                  data-testid="membership-add-role"
                >
                  <option value="administrator">Administrator</option>
                  <option value="analyst">Analyst</option>
                  <option value="view_only">View-Only</option>
                </select>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setAddOpen(false);
                      setAddScopeType("tenant");
                      setAddScopeId("");
                      setAddRole("view_only");
                    }}
                    disabled={busy}
                    className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
                  >Cancel</button>
                  <button
                    onClick={handleSaveClick}
                    disabled={busy}
                    data-testid="membership-add-save"
                    className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40"
                  >Grant Access</button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setAddOpen(true); setErr(""); }}
              disabled={busy}
              data-testid="memberships-add-btn"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-navy border border-navy rounded-[3px] hover:bg-navy hover:text-white transition-colors"
            >
              <Plus size={12} /> Grant access
            </button>
          )}
        </div>
      </div>
    </div>

    {/* 2026-02-16 (Dave) — branded replacement for window.confirm() on
        membership revoke. Rendered outside the drawer's backdrop
        wrapper so its own backdrop click doesn't bubble to onClose. */}
    <ConfirmDialog
      open={!!removing}
      title="Revoke this account?"
      tone="danger"
      confirmLabel="Revoke"
      message={
        removing ? (
          <>
            <b>{user.full_name}</b> will lose access to{" "}
            <b>
              {removing.scope_type === "platform"
                ? "uReserve Platform"
                : (allFirms.find((x) => x.tenant_id === removing.scope_id)?.name || "this firm")}
            </b>
            . You can grant it back later.
          </>
        ) : null
      }
      onConfirm={confirmRemoveMembership}
      onCancel={() => setRemoving(null)}
      testid="membership-remove-confirm"
    />

    {/* 2026-02-19 (Dave) — speed bump before granting Platform Admin.
        Only appears when scope_type=platform AND role=administrator.
        Analyst / View-Only platform grants skip this because they're
        still bounded roles. */}
    <ConfirmDialog
      open={confirmingPlatformGrant}
      title="Grant Platform Administrator?"
      tone="danger"
      confirmLabel="Yes, grant Platform Admin"
      message={
        <>
          You are about to give <b>{user.full_name}</b> ({user.email}){" "}
          the same level of access you have. They will be able to:
          <ul className="mt-2 list-disc list-inside text-[12px] text-ink">
            <li>See and edit <b>every</b> firm and association in the system</li>
            <li>Create and revoke any other admin&apos;s access</li>
            <li>Change platform-wide settings</li>
          </ul>
          <div className="mt-2 text-[11.5px] text-slate italic">
            You can revoke this at any time from their memberships list.
          </div>
        </>
      }
      onConfirm={() => { setConfirmingPlatformGrant(false); add(); }}
      onCancel={() => setConfirmingPlatformGrant(false)}
      testid="platform-grant-confirm"
    />
    </>
  );
}

// ---------- Nuclear delete modal · Slice E5u (2026-02-09) -----------------
// Guardrails:
//   * Type the target email exactly to enable the red DELETE button
//   * Backend re-validates: not self, not platform admin, not firm admin,
//     actor must share a tenant with target unless platform admin
// The modal is only opened for eligible rows (see rowCanBeNuked below).
function NuclearDeleteModal({ user, onClose, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // 2026-02-25 (Dave) — Users pre-provisioned without an email are now
  // deletable by echoing their FULL NAME instead of their email. Same
  // "type-to-confirm" guardrail, adapted to whichever identifier the
  // user actually has.
  const hasEmail = !!(user.email && user.email.trim());
  const expected = (hasEmail ? user.email : user.full_name || "").trim();
  const label = hasEmail ? "email" : "full name";
  const ok = expected.length > 0 && confirmText.trim().toLowerCase() === expected.toLowerCase();

  const doDelete = async () => {
    if (!ok) return;
    setBusy(true); setErr("");
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}`, {
        params: { confirm_email: expected },
      });
      onDone();
    } catch (e) {
      setErr(apiDetail(e, "Delete failed."));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid="nuclear-delete-modal-backdrop"
    >
      <div
        className="bg-white border-2 border-coral shadow-2xl w-[520px] max-w-[92vw] rounded-[8px]"
        style={{ borderRadius: 8, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="nuclear-delete-modal"
      >
        <div className="flex items-center justify-between px-4 py-3 bg-coral text-white">
          <div className="flex items-center gap-2">
            <Trash2 size={18} />
            <span className="font-bold text-[14px] tracking-wide uppercase">
              Permanent Delete
            </span>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-80"
            data-testid="nuclear-delete-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-start gap-2 border border-coral bg-[#FEF6F3] p-3 rounded-[3px]">
            <AlertTriangle size={16} className="text-coral shrink-0 mt-0.5" />
            <div className="text-[12px] text-ink leading-snug">
              This will <b>permanently delete</b>{" "}
              <span className="font-mono">{hasEmail ? user.email : user.full_name}</span> and all their
              memberships. The user&apos;s authored scenarios and elements will
              remain in the database with an orphaned author reference.
              <br />
              <b className="text-coral">This action cannot be undone.</b>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1">
              Type the user&apos;s {label} to confirm
            </div>
            <div className="text-[10.5px] text-slate font-mono mb-1">
              Expected: <b>{expected}</b>
            </div>
            <input
              type="text"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expected}
              className={`w-full border-2 px-2 py-1.5 text-[12.5px] font-mono outline-none rounded-[3px] ${
                ok ? "border-coral" : "border-rule focus:border-navy"
              }`}
              data-testid="nuclear-delete-confirm-input"
            />
          </div>
          {err && (
            <div
              className="text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2]"
              data-testid="nuclear-delete-error"
            >
              {err}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 bg-navy-wash border-t border-rule">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
            data-testid="nuclear-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={doDelete}
            disabled={!ok || busy}
            className="px-3 py-1.5 text-[12px] font-bold text-white rounded-[3px] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: ok ? "#EA6A47" : "#94A3B8" }}
            data-testid="nuclear-delete-confirm"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {busy ? "Deleting…" : "Permanent Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Cumulative active time → "0m", "47m", "3h 12m", "125h".
function fmtActive(sec) {
  const m = Math.floor((sec || 0) / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h >= 100 ? `${h}h` : `${h}h ${m % 60}m`;
}

const fmtCT = (ts) => (ts ? new Date(ts).toLocaleString("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "") : "—");

function SortTh({ label, sortKey, sort, onSort, title }) {
  return (
    <th
      onClick={() => onSort(sortKey)}
      title={title}
      className="px-3 py-1 text-right text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral whitespace-nowrap"
      data-testid={`th-${sortKey}`}
    >
      {label}
      {sort.key === sortKey && <span className="ml-1 text-coral">{sort.dir === "asc" ? "▲" : "▼"}</span>}
    </th>
  );
}

// ---------- Main screen ---------------------------------------------------

// 2026-02-27 (Dave) — Effective role helpers.
//
// The legacy `u.role` column is unreliable — it stays "administrator"
// even when a user's actual memberships are all view_only (e.g. Mitch
// Gassen: `u.role=administrator` but only membership is `view_only @
// HOA Assist`). The pill + hint must reflect the user's ACTUAL highest
// membership role, not the legacy column. Rank: administrator > analyst
// > view_only. Ties broken by scope breadth: platform > tenant > client.
const _ROLE_RANK = { administrator: 0, analyst: 1, view_only: 2 };
const _SCOPE_RANK = { platform: 0, tenant: 1, client: 2 };

function effectiveRole(u) {
  const mems = u?.memberships || [];
  if (!mems.length) return u?.role || "view_only";
  let best = null;
  for (const m of mems) {
    const rk = _ROLE_RANK[m.role];
    if (rk === undefined) continue;
    if (best === null || rk < _ROLE_RANK[best]) best = m.role;
  }
  return best || u?.role || "view_only";
}

function effectiveScope(u) {
  // Returns { scope_type, scope_id } of the membership with the highest
  // role (rank 0 wins; ties broken by scope breadth). null if the user
  // has no memberships.
  const mems = u?.memberships || [];
  if (!mems.length) return null;
  let best = null;
  for (const m of mems) {
    if (!(m.role in _ROLE_RANK)) continue;
    if (
      best === null ||
      _ROLE_RANK[m.role] < _ROLE_RANK[best.role] ||
      (_ROLE_RANK[m.role] === _ROLE_RANK[best.role] &&
        _SCOPE_RANK[m.scope_type] < _SCOPE_RANK[best.scope_type])
    ) {
      best = m;
    }
  }
  return best;
}

// "Last active 3 min ago" — hover text for the Users-tab logged-in dot.
function lastActiveLabel(ts) {
  if (!ts) return "No activity recorded";
  const min = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (min < 1) return "Last active just now";
  if (min < 60) return `Last active ${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `Last active ${hr} hr${hr === 1 ? "" : "s"} ago`;
  const d = Math.floor(hr / 24);
  return `Last active ${d} day${d === 1 ? "" : "s"} ago`;
}

// Fallback split for users not yet given separate name parts; suffixes stay with the last name.
const NAME_SUFFIXES = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv"]);
function splitName(fullName) {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return [parts.join(" "), ""];
  let cut = parts.length - 1;
  if (NAME_SUFFIXES.has(parts[cut].toLowerCase()) && cut > 1) cut -= 1;
  return [parts.slice(0, cut).join(" "), parts.slice(cut).join(" ")];
}
function displayLastFirst(u) {
  const [f, l] = u.last_name || u.first_name ? [u.first_name || "", u.last_name || ""] : splitName(u.full_name);
  return l ? (f ? `${l}, ${f}` : l) : f;
}

export default function UsersScreen() {
  const { user: me } = useAuth();
  const myUserId = me?.user_id;

  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  // 2026-02-25 (Dave) — associations pulled once at screen level so
  // client-scope memberships can be rendered by name (association +
  // firm) instead of a truncated aid slug.
  const [assocsAll, setAssocsAll] = useState([]);
  const assocById = useMemo(() => {
    const m = new Map();
    for (const a of assocsAll) m.set(a.aid, a);
    return m;
  }, [assocsAll]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [roleFilter, setRoleFilter] = useState(null); // null = all roles

  // "Logged in" uses `last_seen_at` (bumped in the background at most once a
  // minute per user on any authenticated request); falls back to last_login_at.
  // 2026-10-10 (Dave) — dot goes grey after 15 min without activity.
  const LOGGED_IN_WINDOW_MIN = 15;
  const isLoggedIn = (u) => {
    // The viewer is by definition signed in — their own heartbeat may land after this list loads.
    if (me && u?.user_id === me.user_id) return true;
    const ts = u?.last_seen_at || u?.last_login_at;
    if (!ts) return false;
    const ageMs = Date.now() - new Date(ts).getTime();
    return ageMs < LOGGED_IN_WINDOW_MIN * 60 * 1000;
  };

  const [editor, setEditor] = useState(null);   // { initial: user | null }
  const [resetting, setResetting] = useState(null);
  const [drawerFor, setDrawerFor] = useState(null);
  const [nuking, setNuking] = useState(null);   // Slice E5u — nuclear-delete target
  // 2026-02-16 (Dave) — pending deactivate/reactivate confirmation.
  // Held here so the branded ConfirmDialog can render at the screen
  // root instead of using the browser-native confirm().
  const [toggling, setToggling] = useState(null);
  // Branded one-button "OK" ack dialog — used for the "cannot act on
  // yourself" validation message that used to be a window.alert().
  const [selfActionAlert, setSelfActionAlert] = useState("");

  // 2026-03-08 (Dave) — Drill-through filter from Admin → Associations.
  // Clicking a Users count on that screen stashes the aid+display
  // name in sessionStorage; we pick it up on mount, apply it as a
  // filter, then clear the storage so a plain refresh no longer
  // shows the filter (avoids sticky filters after page reload).
  const [assocFilterAid, setAssocFilterAid] = useState(null);
  const [assocFilterName, setAssocFilterName] = useState("");
  // 2026-03-08 (Dave) — Firm slicer for the Users list. Can be set
  // three ways: (1) drill-through from Admin → Firms Users count,
  // (2) drill-through from Admin → Associations (implied by the
  // association's firm), (3) manual dropdown selection above the
  // table. Empty string == "All firms".
  const [firmFilterTid, setFirmFilterTid] = useState("");
  useEffect(() => {
    try {
      const aid = sessionStorage.getItem("ureserve:users-filter-aid");
      const name = sessionStorage.getItem("ureserve:users-filter-name");
      const tid = sessionStorage.getItem("ureserve:users-filter-tenant-id");
      if (aid) {
        setAssocFilterAid(aid);
        setAssocFilterName(name || "");
        sessionStorage.removeItem("ureserve:users-filter-aid");
        sessionStorage.removeItem("ureserve:users-filter-name");
      }
      if (tid) {
        setFirmFilterTid(tid);
        sessionStorage.removeItem("ureserve:users-filter-tenant-id");
        sessionStorage.removeItem("ureserve:users-filter-firm-name");
      }
    } catch { /* sessionStorage unavailable — ignore */ }
  }, []);

  // Slice E5u — eligibility for the nuclear-delete button:
  //   * Never the current user's own row
  //   * Actor must be Platform Admin or Firm Admin (either implies access)
  //   * Target must NOT be a Platform Admin or Firm Admin (memberships)
  //   * For Firm Admins (non-platform), the target must share at least one
  //     tenant scope with the actor
  const actorIsPlatform = isPlatformAdmin(me);
  const actorIsAnyAdmin = isPlatformOrFirmAdmin(me);
  // Association-only admins (no firm/platform admin) — scoped to their own associations.
  const assocOnlyAids = useMemo(() => {
    if (actorIsAnyAdmin) return null;
    const s = assocAdminAids(me);
    return s.size ? s : null;
  }, [me, actorIsAnyAdmin]);
  const actorFirmTenants = useMemo(() => {
    const s = new Set();
    (me?.memberships || []).forEach((m) => {
      if (m?.scope_type === "tenant" && m?.role === "administrator" && m?.scope_id) {
        s.add(String(m.scope_id));
      }
    });
    return s;
  }, [me]);

  const rowCanBeNuked = (u) => {
    if (u.user_id === myUserId) return false;
    if (assocOnlyAids) {
      // Only non-admin users whose every membership is on an association this actor administers.
      const ms = u.memberships || [];
      return ms.length > 0 && ms.every((m) => m.scope_type === "client" && m.role !== "administrator" && assocOnlyAids.has(String(m.scope_id)));
    }
    if (!actorIsAnyAdmin) return false;
    const targetIsPlatformAdmin = (u.memberships || []).some(
      (m) => m.scope_type === "platform" && m.role === "administrator",
    );
    if (targetIsPlatformAdmin) return false;
    const targetIsFirmAdmin = (u.memberships || []).some(
      (m) => m.scope_type === "tenant" && m.role === "administrator",
    );
    if (targetIsFirmAdmin) return false;
    if (actorIsPlatform) return true;
    // Firm admin: must share a tenant with target
    const targetTenants = new Set(
      (u.memberships || [])
        .filter((m) => m.scope_type === "tenant")
        .map((m) => String(m.scope_id)),
    );
    for (const t of actorFirmTenants) if (targetTenants.has(t)) return true;
    return false;
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      // 2026-02-25 (Dave) — Wrapped in retryOnReloadHiccup so a
      // uvicorn hot-reload window doesn't paint a scary 404 banner.
      const [u, f, a] = await retryOnReloadHiccup(() =>
        Promise.all([
          axios.get(`${API}/admin/users`, { params: { limit: 500, include_inactive: includeInactive, q: search || undefined } }),
          axios.get(`${API}/admin/firms?include_inactive=true`).catch(() => ({ data: [] })),
          axios.get(`${API}/associations`),
        ]),
      );
      setUsers(Array.isArray(u.data) ? u.data : []);
      setFirms(Array.isArray(f.data) ? f.data : []);
      setAssocsAll(Array.isArray(a.data) ? a.data : []);
    } catch (e) {
      setErr(apiDetail(e, "Failed to load users."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [includeInactive]);

  // Slice E5v (2026-02-09) — Firm column + sort.
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const firmNameById = useMemo(() => {
    const m = new Map();
    (firms || []).forEach((f) => m.set(String(f.tenant_id || f.aid || f.id), f.name || f.tenant_name));
    return m;
  }, [firms]);
  const firmNameForUser = (u) => {
    const t = (u.memberships || []).find((mm) => mm.scope_type === "tenant" && mm.scope_id);
    if (t) return firmNameById.get(String(t.scope_id)) || "";
    const platform = (u.memberships || []).find((mm) => mm.scope_type === "platform");
    if (platform) return "Platform";
    // 2026-02-25 (Dave) — client-scope memberships: resolve to the
    // association's firm so the FIRM column reads correctly for
    // association-only users like "Chris @ Adelaide Landing Master".
    const client = (u.memberships || []).find((mm) => mm.scope_type === "client" && mm.scope_id);
    if (client) {
      const a = assocById.get(String(client.scope_id));
      if (a && a.tenant_id) {
        return firmNameById.get(String(a.tenant_id)) || "";
      }
      return "Association scope";
    }
    return "";
  };

  // 2026-03-08 (Dave) — companion to `firmNameForUser` but returns
  // the tenant_id used by the Firm dropdown filter. Definition
  // matches the backend Firms Users rollup exactly: only users
  // whose CLIENT-scope memberships resolve to an association owned
  // by the firm are considered "users of that firm". Firm admins
  // (tenant-scope memberships) are surfaced via the Firm column /
  // Role pill on the unfiltered list, not by this filter, so that
  // clicking the "N users" cell on Admin → Firms lands on exactly
  // N rows here.
  const firmTenantIdsForUser = (u) => {
    const set = new Set();
    for (const mm of u.memberships || []) {
      if (mm.scope_type === "client" && mm.scope_id) {
        const a = assocById.get(String(mm.scope_id));
        if (a && a.tenant_id) set.add(String(a.tenant_id));
      }
    }
    return set;
  };

  const onSort = (key) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  const filtered = useMemo(() => {
    // 2026-03-08 (Dave) — apply the drill-through / dropdown filters
    // BEFORE sorting so counts + labels reflect the narrowed set.
    // Assoc filter: user has a client-scope membership on the aid,
    // which is exactly what the Associations table's Users count
    // counted. Firm filter: user has ANY tenant-scope or client-
    // scope-resolved-to-firm match against the selected tenant_id.
    let base = users;
    if (assocFilterAid) {
      base = base.filter((u) =>
        (u.memberships || []).some(
          (mm) => mm.scope_type === "client" && String(mm.scope_id) === String(assocFilterAid),
        ),
      );
    }
    if (firmFilterTid) {
      base = base.filter((u) => firmTenantIdsForUser(u).has(String(firmFilterTid)));
    }
    if (roleFilter) {
      base = base.filter((u) => effectiveRole(u) === roleFilter);
    }
    const arr = [...base];
    const { key, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    // 2026-02-19 (Dave) — sort keys extended: `name` (user full_name) and
    // `role` (with a fixed hierarchy so pills stack in intuitive order
    // rather than raw alphabetical, e.g. Administrator > Analyst >
    // View-Only when sorting ascending).
    const ROLE_RANK = { administrator: 0, analyst: 1, view_only: 2 };
    // 2026-02-27 (Dave) — pill + hint now reflect the user's HIGHEST
    // membership role, not the legacy `u.role` column. Sort by that
    // computed role so the table ranks reach honestly (Justin
    // Porter's analyst @ Product Development sits with the analysts,
    // not the administrators).
    const val = (u) => {
      if (key === "firm") return (firmNameForUser(u) || "").toLowerCase();
      if (key === "name") return displayLastFirst(u).toLowerCase();
      if (key === "role") return ROLE_RANK[effectiveRole(u)] ?? 99;
      // 2026-03-08 (Dave) — Accounts sort. Ascending puts users
      // with the fewest accounts first (great for spotting
      // orphaned or under-provisioned staff); descending surfaces
      // the busiest reach — that's the "who touches everything"
      // question admins ask most often.
      if (key === "accounts") return (u.memberships || []).length;
      if (key === "last_login") {
        const ts = u?.last_login_at;
        return ts ? new Date(ts).getTime() : null;
      }
      if (key === "active_time") return Number(u.total_active_seconds || 0);
      if (key === "sessions") return Number(u.login_count || 0);
      if (key === "last_active") {
        const ts = u?.last_seen_at;
        return ts ? new Date(ts).getTime() : null;
      }
      return "";
    };
    arr.sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      // Null / empty values sort last regardless of direction so
      // never-logged-in users always cluster at the bottom.
      const aEmpty = av === "" || av === null || av === undefined;
      const bEmpty = bv === "" || bv === null || bv === undefined;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * mult;
      }
      return String(av).localeCompare(String(bv)) * mult;
    });
    return arr;
  }, [users, sort, firmNameById, assocFilterAid, firmFilterTid, assocById, roleFilter]);

  const toggleActive = (u) => {
    if (u.user_id === myUserId) {
      setSelfActionAlert("You cannot deactivate yourself. Ask another Platform Admin.");
      return;
    }
    // 2026-02-16 — swap window.confirm() for the branded ConfirmDialog.
    // The dialog fires its onConfirm when the user clicks the action
    // button; the actual PATCH/POST runs inside that handler below.
    setToggling(u);
  };

  const confirmToggleActive = async () => {
    const u = toggling;
    if (!u) return;
    const action = u.is_active ? "deactivate" : "reactivate";
    try {
      if (u.is_active) {
        await axios.post(`${API}/admin/users/${u.user_id}/deactivate`);
      } else {
        await axios.patch(`${API}/admin/users/${u.user_id}`, { is_active: true });
      }
      setToggling(null);
      await load();
    } catch (e) {
      // Re-throw so ConfirmDialog surfaces the error in its coral banner
      // and keeps the dialog open. Fallback message mirrors the prior
      // window.alert() text.
      const msg = e?.response?.data?.detail || `${action} failed.`;
      throw new Error(msg);
    }
  };

  return (
    // 2026-08-18 (Dave) — flip outer scroll off so the sticky thead in
    // the table below can pin against the top of the table's own scroll
    // container. The header block + search bar stay fixed at the top of
    // the screen; only the table body scrolls.
    <div className="flex-1 overflow-hidden flex flex-col p-6" data-testid="screen-users">
      <div className="max-w-[1600px] mx-auto flex flex-col gap-3 flex-1 min-h-0 w-full">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 pb-2 mb-1" style={{ borderBottom: "2px solid #1C4E80" }}>
            <UsersIcon size={18} className="text-navy" />
            <h1 className="text-[18px] font-bold text-navy tracking-tight">Users</h1>
            <span className="ml-2 text-[10px] tracking-[0.12em] uppercase font-semibold text-slate">Platform Admin</span>
          </div>
          <p className="text-[12.5px] text-slate">
            Create staff accounts, reset passwords, and grant / revoke access to specific firms. All actions are logged in the Audit trail.
          </p>
        </div>

        {/* Search bar — compacted 2026-08-18 (Dave) */}
        <div className="flex items-center gap-2 bg-white border border-rule rounded-[3px] shadow-sm p-2">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search by name or email…"
              data-testid="users-search"
              className="w-full border border-rule rounded-[3px] pl-7 pr-2 py-1 text-[12.5px] focus:outline-none focus:border-navy"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[11.5px] text-slate">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              data-testid="users-include-inactive"
            />
            Show inactive
          </label>
          <div className="flex items-center gap-1" data-testid="users-role-chips">
            {[[null, "All"], ["administrator", "Admins"], ["analyst", "Analysts"], ["view_only", "View-only"]].map(([key, label]) => {
              const on = roleFilter === key;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setRoleFilter(key)}
                  aria-pressed={on}
                  className={"px-2 py-0.5 text-[11px] font-semibold rounded-full border transition-colors " + (on ? "bg-navy text-white border-navy" : "bg-white text-slate border-rule hover:border-navy hover:text-navy")}
                  data-testid={`users-role-chip-${key || "all"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {/* 2026-03-08 (Dave) — Firm slicer. Same option list as the
              Firm column on the table below (all firms the actor can
              see). "All firms" clears the filter. */}
          <select
            value={firmFilterTid}
            onChange={(e) => setFirmFilterTid(e.target.value)}
            className="px-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:outline-none focus:border-navy"
            data-testid="users-firm-filter"
            title="Filter users by firm"
          >
            <option value="">All firms</option>
            {[...(firms || [])]
              .filter((f) => f && (f.tenant_id))
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((f) => (
                <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
              ))}
          </select>
          <button
            onClick={load}
            className="px-3 py-1 text-[12px] font-semibold bg-white border border-rule rounded-[3px] hover:border-navy"
            data-testid="users-refresh"
          >Refresh</button>
          <button
            onClick={() => setEditor({ initial: null })}
            className="px-3 py-1 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] flex items-center gap-1.5"
            data-testid="users-new"
          >
            <Plus size={13} /> New User
          </button>
        </div>

        {/* 2026-03-08 (Dave) — Active filter chips. Show a chip for
            each active narrowing (drill-through association and/or
            firm dropdown). Clearing a chip removes only that filter. */}
        {(assocFilterAid || firmFilterTid) && (
          <div
            className="flex items-center gap-2 text-[12px] flex-wrap"
            data-testid="users-filter-chips"
          >
            <span className="text-slate">Showing users on</span>
            {assocFilterAid && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[3px] border border-navy bg-navy-wash text-navy font-semibold"
                data-testid="users-assoc-filter-chip"
              >
                {assocFilterName || "selected association"}
                <button
                  type="button"
                  onClick={() => { setAssocFilterAid(null); setAssocFilterName(""); }}
                  className="hover:text-coral transition-colors"
                  title="Clear association filter"
                  data-testid="users-assoc-filter-clear"
                >
                  <X size={12} strokeWidth={2.4} />
                </button>
              </span>
            )}
            {firmFilterTid && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[3px] border border-navy bg-navy-wash text-navy font-semibold"
                data-testid="users-firm-filter-chip"
              >
                {firmNameById.get(String(firmFilterTid)) || "selected firm"}
                <button
                  type="button"
                  onClick={() => setFirmFilterTid("")}
                  className="hover:text-coral transition-colors"
                  title="Clear firm filter"
                  data-testid="users-firm-filter-clear"
                >
                  <X size={12} strokeWidth={2.4} />
                </button>
              </span>
            )}
            <span className="text-slate">
              ({filtered.length} {filtered.length === 1 ? "user" : "users"})
            </span>
          </div>
        )}

        {err && <div className="text-[12px] text-coral border border-coral px-3 py-1 rounded-[3px] bg-[#FEF5F2]" data-testid="users-error">{err}</div>}

        {/* Table — thead pinned via sticky top-0 inside its own scroll
            container. Row heights compacted (py-2 → py-1). */}
        <div className="bg-white border border-rule rounded-[8px] shadow-sm overflow-hidden flex-1 min-h-0">
          <div className="h-full overflow-auto">
          <table className="w-full">
            <thead
              className="bg-navy-wash sticky top-0 z-10 border-b-2 border-navy"
              style={{ boxShadow: "0 2px 0 #1C4E80" }}
            >
              <tr>
                {/* 2026-03-08 (Dave) — Column order: Name → Memberships
                    → Firm → Email → Role → Last Login → Status →
                    Actions. Dave prefers scanning by person first. */}
                <th
                  onClick={() => onSort("name")}
                  className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral"
                  data-testid="th-name"
                  style={{ width: 160 }}
                >
                  Name
                  {sort.key === "name" && (
                    <span className="ml-1 text-coral">
                      {sort.dir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => onSort("accounts")}
                  className="px-1 py-1 text-center text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral"
                  data-testid="th-accounts"
                  style={{ width: 72 }}
                >
                  Accounts
                  {sort.key === "accounts" && (
                    <span className="ml-1 text-coral">
                      {sort.dir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => onSort("firm")}
                  className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral"
                  data-testid="th-firm"
                >
                  Firm
                  {sort.key === "firm" && (
                    <span className="ml-1 text-coral">
                      {sort.dir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy">Email</th>
                <th
                  onClick={() => onSort("role")}
                  className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral"
                  data-testid="th-role"
                  style={{ width: 240 }}
                >
                  Role
                  {sort.key === "role" && (
                    <span className="ml-1 text-coral">
                      {sort.dir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => onSort("last_login")}
                  className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy cursor-pointer select-none hover:text-coral"
                  data-testid="th-last-login"
                >
                  <div className="flex items-baseline gap-2">
                    <span>
                      Last Login
                      {sort.key === "last_login" && (
                        <span className="ml-1 text-coral">
                          {sort.dir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                    <span
                      className="normal-case tracking-normal text-[9.5px] font-semibold text-slate"
                      data-testid="users-logged-in-count"
                    >
                      Logged In (<span className="text-[#2E7D5B]">{filtered.filter(isLoggedIn).length}</span>)
                    </span>
                  </div>
                </th>
                <SortTh label="Active Time" sortKey="active_time" sort={sort} onSort={onSort} title="Total time spent actively using the app (idle time not counted)" />
                <SortTh label="Sessions" sortKey="sessions" sort={sort} onSort={onSort} title="Number of sign-ins (counted from 2026-11-03)" />
                <SortTh label="Last Active" sortKey="last_active" sort={sort} onSort={onSort} />
                <th className="px-3 py-1 text-center text-[10px] tracking-[0.12em] uppercase font-bold text-navy">Status</th>
                <th className="px-3 py-1 text-right text-[10px] tracking-[0.12em] uppercase font-bold text-navy">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-[12px] text-slate"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading users…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-[12px] text-slate italic">No users match.</td></tr>
              )}
              {!loading && filtered.map((u) => {
                const isMe = u.user_id === myUserId;
                const firmLabel = firmNameForUser(u);
                return (
                  <tr key={u.user_id} className={`border-b border-rule ${u.is_active ? "" : "opacity-60"}`} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-3 py-1 text-[12.5px] text-ink font-semibold whitespace-nowrap" style={{ width: 160 }}>
                      {displayLastFirst(u)}
                      {isMe && <span className="ml-2 text-[10px] text-slate uppercase tracking-[0.1em]">(you)</span>}
                    </td>
                    <td className="px-1 py-1 text-center" style={{ width: 72 }}>
                      <button
                        type="button"
                        onClick={() => setDrawerFor(u)}
                        data-testid={`user-memberships-${u.user_id}`}
                        className="inline-flex items-center justify-center text-navy px-2 py-0 leading-[18px] text-[11.5px] border border-rule rounded-[3px] hover:border-navy"
                      >
                        {u.memberships.length}
                      </button>
                    </td>
                    <td
                      className="px-3 py-1 text-left text-[12px] text-navy font-semibold whitespace-nowrap"
                      data-testid={`user-firm-${u.user_id}`}
                    >
                      {firmLabel || <span className="text-slate italic">—</span>}
                    </td>
                    <td className="px-3 py-1 text-[12px] font-mono">
                      {u.email ? (
                        <span className="text-slate">{u.email}</span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10.5px] tracking-[0.06em] uppercase font-semibold text-coral"
                          data-testid={`user-no-email-${u.user_id}`}
                          title="No email yet — add one and press Send Invite"
                        >
                          <Mail size={10} /> invite pending
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1">
                      <div className="flex flex-row items-center justify-start gap-2 min-w-0">
                        {/* 2026-02-27 (Dave) — pill = effective role
                            (highest across all memberships), not the
                            legacy `u.role`. Prevents cases like
                            Justin Porter (`u.role=administrator` but
                            all memberships are analyst / view_only)
                            from misrepresenting the user's actual
                            reach. */}
                        <RoleBadge role={effectiveRole(u)} />
                        {(() => {
                          const scope = effectiveScope(u);
                          if (!scope) return null;
                          if (scope.scope_type === "platform") {
                            return <span className="text-[9.5px] tracking-[0.06em] uppercase text-slate" data-testid={`user-scope-hint-${u.user_id}`}>Platform-wide</span>;
                          }
                          if (scope.scope_type === "tenant") {
                            const nm = firmNameById.get(String(scope.scope_id));
                            return (
                              <span
                                className="text-[9.5px] tracking-[0.06em] uppercase text-slate truncate max-w-[140px] block"
                                title={nm || "Firm-wide"}
                                data-testid={`user-scope-hint-${u.user_id}`}
                              >
                                {nm || "Firm-wide"}
                              </span>
                            );
                          }
                          const a = assocById.get(String(scope.scope_id));
                          const nm = a?.name;
                          return (
                            <span
                              className="text-[9.5px] tracking-[0.06em] uppercase text-slate truncate max-w-[140px] block"
                              title={nm || "Association only"}
                              data-testid={`user-scope-hint-${u.user_id}`}
                            >
                              {nm || "Association only"}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-1 text-[11.5px] text-slate font-mono whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          data-testid={`user-loggedin-dot-${u.user_id}`}
                          title={lastActiveLabel(u?.last_seen_at || u?.last_login_at)}
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: isLoggedIn(u) ? "#2E7D5B" : "transparent",
                            border: isLoggedIn(u) ? "none" : "1px solid #C9D6E2",
                            flexShrink: 0,
                          }}
                        />
                        {fmtCT(u.last_login_at)}
                      </span>
                    </td>
                    <td className="px-3 py-1 text-right text-[11.5px] text-navy font-mono font-semibold tabular-nums whitespace-nowrap" data-testid={`user-active-time-${u.user_id}`}>
                      {fmtActive(u.total_active_seconds)}
                    </td>
                    <td className="px-3 py-1 text-right text-[11.5px] text-slate font-mono tabular-nums" data-testid={`user-sessions-${u.user_id}`}>
                      {u.login_count || 0}
                    </td>
                    <td className="px-3 py-1 text-right text-[11.5px] text-slate font-mono whitespace-nowrap" data-testid={`user-last-active-${u.user_id}`}>
                      {fmtCT(u.last_seen_at)}
                    </td>
                    <td className="px-3 py-1 text-center">
                      {u.is_active
                        ? <span className="text-[10.5px] uppercase tracking-[0.1em] font-bold text-[#2E7D5B]">Active</span>
                        : <span className="text-[10.5px] uppercase tracking-[0.1em] font-bold text-slate">Inactive</span>
                      }
                    </td>
                    <td className="px-3 py-1">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn onClick={() => setEditor({ initial: u })} title="Edit user" testid={`user-edit-${u.user_id}`}>
                          <Pencil size={12} />
                        </IconBtn>
                        <IconBtn onClick={() => setResetting(u)} title="Reset password" testid={`user-reset-${u.user_id}`}>
                          <KeyRound size={12} />
                        </IconBtn>
                        <IconBtn
                          onClick={() => toggleActive(u)}
                          disabled={isMe}
                          title={isMe ? "You cannot deactivate yourself" : (u.is_active ? "Deactivate" : "Reactivate")}
                          danger={u.is_active}
                          testid={`user-toggle-${u.user_id}`}
                        >
                          {u.is_active ? <PowerOff size={12} /> : <Power size={12} />}
                        </IconBtn>
                        {/* Slice E5u — nuclear delete. Hidden unless the
                            row is eligible per rowCanBeNuked(). */}
                        {/* Slice E5x (2026-02-09) — render an invisible
                            placeholder when this row isn't eligible for
                            delete, so the trailing icons in other rows
                            stay column-aligned. */}
                        {rowCanBeNuked(u) ? (
                          <IconBtn
                            onClick={() => setNuking(u)}
                            title="Permanent delete (irreversible)"
                            danger
                            testid={`user-nuke-${u.user_id}`}
                          >
                            <Trash2 size={12} />
                          </IconBtn>
                        ) : (
                          <span
                            aria-hidden="true"
                            className="inline-block"
                            style={{ width: 32, height: 32 }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {editor && <UserEditorModal initial={editor.initial} allFirms={firms} allAssocs={assocsAll} actorIsPlatform={actorIsPlatform} assocOnlyAids={assocOnlyAids} onClose={() => setEditor(null)} onSaved={load} onOpenMemberships={(u) => { setEditor(null); setDrawerFor(u); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
      {drawerFor && <MembershipsDrawer user={drawerFor} allFirms={firms} assocsAll={assocsAll} assocOnlyAids={assocOnlyAids} onClose={() => setDrawerFor(null)} onChanged={load} />}
      {nuking && (
        <NuclearDeleteModal
          user={nuking}
          onClose={() => setNuking(null)}
          onDone={() => { setNuking(null); load(); }}
        />
      )}
      {/* 2026-02-16 (Dave) — branded replacement for window.confirm() on
          the deactivate/reactivate flow. Tone flips coral for deactivate
          (destructive) and navy for reactivate (info). */}
      <ConfirmDialog
        open={!!toggling}
        title={toggling?.is_active ? "Deactivate User" : "Reactivate User"}
        tone={toggling?.is_active ? "danger" : "neutral"}
        confirmLabel={toggling?.is_active ? "Deactivate" : "Reactivate"}
        message={
          toggling ? (
            toggling.is_active ? (
              <>
                Deactivate <b>{toggling.full_name}</b>? They will lose access
                immediately and any active session will end on their next
                request. You can reactivate them later.
              </>
            ) : (
              <>
                Reactivate <b>{toggling.full_name}</b>? They will regain the
                access granted by their existing memberships as soon as they
                sign in again.
              </>
            )
          ) : null
        }
        onConfirm={confirmToggleActive}
        onCancel={() => setToggling(null)}
        testid="user-toggle-confirm"
      />

      {/* 2026-02-16 (Dave) — branded replacement for the "cannot
          deactivate yourself" window.alert(). One-button acknowledge. */}
      <ConfirmDialog
        open={!!selfActionAlert}
        title="Action blocked"
        tone="danger"
        confirmLabel="OK"
        hideCancel
        message={selfActionAlert}
        onConfirm={() => setSelfActionAlert("")}
        onCancel={() => setSelfActionAlert("")}
        testid="self-action-alert"
      />
    </div>
  );
}
